// src/components/Comment.tsx
import React, { useState } from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { RedditComment, RedditMoreComments } from '../types/reddit';
import CommentTree from './CommentTree';
import { rewriteXLink, rewriteRedditLinks } from '../utils/linkUtils';
// Import lightbox
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";

interface CommentProps {
    comment: RedditComment;
    postAuthor: string;
    onLoadMore: (moreData: RedditMoreComments['data']) => void;
    loadingMoreId: string | null;
    indentSize: number; // Now passed from CommentTree, likely 20px
}

// Helper function to check if a URL looks like an image
const isImageUrl = (url: string): boolean => {
    if (!url) return false;
    // Relaxed regex to catch more image types potentially, though backend handles most
    if (/\.(jpg|jpeg|png|gif|webp|bmp|tiff|svg|avif|jxl)$/i.test(url)) {
        return true;
    }
    try {
        // Check common image hosting domains used on Reddit
        if (!url.startsWith('http://') && !url.startsWith('https://')) return false;
        const hostname = new URL(url).hostname.toLowerCase();
        return ['preview.redd.it', 'i.redd.it', 'i.imgur.com', 'gfycat.com', 'redgifs.com'].some(domain => hostname.endsWith(domain));
    } catch (e) { return false; }
};

// Helper to format time difference
const timeAgo = (timestamp: number): string => {
    const now = Date.now();
    const secondsPast = Math.floor((now - timestamp * 1000) / 1000);
    if (secondsPast < 60) return `${secondsPast}s ago`;
    if (secondsPast < 3600) return `${Math.floor(secondsPast / 60)}m ago`;
    if (secondsPast <= 86400) return `${Math.floor(secondsPast / 3600)}h ago`;
    const days = Math.floor(secondsPast / 86400);
    return `${days}d ago`;
};

// Helper to render embedded media from special link formats (like Reddit app's internal links)
const renderEmbeddedMedia = (decodedHref: string, children: React.ReactNode, openModal: (url: string) => void): React.ReactNode | null => {
    const lowerHref = decodedHref.toLowerCase();
    // Example: Giphy links often appear as just the ID in markdown `[text](giphy|someId)`
    if (lowerHref.startsWith('giphy|')) {
        const giphyId = decodedHref.substring(6);
        if (giphyId) {
            const giphyUrl = `https://media.giphy.com/media/${giphyId}/giphy.gif`;
            return (
                <ImageWithFallback
                    src={giphyUrl}
                    alt={children?.toString() || 'Giphy GIF'}
                    className="max-w-full h-auto rounded my-2 border dark:border-gray-700"
                    onClick={() => openModal(giphyUrl)}
                    clickable={true}
                />
            );
        }
    }
    // Example: Imgur links `[text](imgur|someId)`
    if (lowerHref.startsWith('imgur|')) {
        const imgurId = decodedHref.substring(6);
        if (imgurId) {
            // Attempt to guess common extensions, might need refinement
            const imgurUrl = `https://i.imgur.com/${imgurId}.gif`; // Assume gif, could be jpg/png
             return (
                <ImageWithFallback
                    src={imgurUrl}
                    alt={children?.toString() || 'Imgur Media'}
                    className="max-w-full h-auto rounded my-2 border dark:border-gray-700"
                    onClick={() => openModal(imgurUrl)}
                    clickable={true}
                />
            );
        }
    }
    // Add more handlers for other special formats if needed
    return null;
};

// Reusable ImageWithFallback component
interface ImageWithFallbackProps extends React.ImgHTMLAttributes<HTMLImageElement> {
    onClick?: React.MouseEventHandler<HTMLImageElement>;
    clickable?: boolean;
}
const ImageWithFallback: React.FC<ImageWithFallbackProps> = ({ src, alt, className, clickable, onClick, ...props }) => {
    const [error, setError] = useState(false);
    const handleError = () => setError(true);
    React.useEffect(() => setError(false), [src]); // Reset error state if src changes
    const combinedClassName = `${className} ${clickable ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''}`;
    if (error || !src) {
        return ( // Simple fallback
            <span className={`text-xs italic text-gray-400 dark:text-gray-500 ${className}`}>[Image unavailable]</span>
        );
    }
    return <img src={src} alt={alt || ''} className={combinedClassName} onError={handleError} loading="lazy" onClick={onClick} {...props} />;
};


const Comment: React.FC<CommentProps> = ({ comment, postAuthor, onLoadMore, loadingMoreId, indentSize }) => {
    const { data } = comment;
    const [isCollapsed, setIsCollapsed] = useState(false);
    // Lightbox state for this comment
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxSlides, setLightboxSlides] = useState<{ src: string }[]>([]);

    const isOp = data.author === postAuthor;

    // Function to open the lightbox for an image within this comment
    const openLightboxForComment = (imageUrl: string | null) => {
        if (imageUrl) {
            setLightboxSlides([{ src: imageUrl }]); // Only one slide per image click in comments
            setLightboxOpen(true);
        }
    };

    const handleToggleCollapse = (e: React.MouseEvent) => {
        // Ensure the click is directly on the trigger area or the [-]/[+] button
        if ((e.target as HTMLElement).closest('.collapse-trigger')) {
            setIsCollapsed(!isCollapsed);
        }
    };

    const hasReplies = data.replies && typeof data.replies === 'object' && data.replies.kind === 'Listing' && data.replies.data?.children?.length > 0;
    // Apply indentation using padding-left on the main container
    const paddingLeft = `${data.depth * indentSize}px`;

    // Custom renderers for ReactMarkdown
    const markdownComponents: Components = {
        // Ensure paragraphs get default rendering (handled by prose)
        // p: ({ node, children }) => <p>{children}</p>, // Usually not needed if prose is applied correctly

        // Ensure blockquotes get default rendering (handled by prose)
        // blockquote: ({ node, children }) => <blockquote>{children}</blockquote>, // Usually not needed

        a: ({ node, href, children, ...props }) => {
            let decodedHref = '';
            if (href) { try { decodedHref = decodeURIComponent(href); } catch (e) { decodedHref = href; } }

            // Check for special embedded formats first
            const embeddedMedia = renderEmbeddedMedia(decodedHref, children, openLightboxForComment);
            if (embeddedMedia) return embeddedMedia;

            // Process regular links (rewrite x.com, reddit internal links)
            const processedHref = rewriteRedditLinks(rewriteXLink(href));

            // Check if the link URL points to an image
            if (processedHref && isImageUrl(processedHref)) {
                return (
                    <ImageWithFallback
                        src={processedHref}
                        alt={children?.toString() || 'Comment image link'}
                        className="max-w-full h-auto rounded my-2 border dark:border-gray-700"
                        onClick={() => openLightboxForComment(processedHref)}
                        clickable={true}
                    />
                );
            }

            // Determine if it's an internal link to the app itself
            const isInternalLink = processedHref?.startsWith(window.location.origin) || processedHref?.startsWith('/');

            // Render standard link
            return (
                <a href={processedHref || '#'} target={isInternalLink ? '_self' : '_blank'} rel="noopener noreferrer" {...props}>
                    {children}
                </a>
            );
        },
        img: ({ node, src, alt, ...props }) => (
            // Render standard images, making them clickable for lightbox
            <ImageWithFallback
                className="my-2 rounded max-w-full h-auto border dark:border-gray-700"
                onClick={() => openLightboxForComment(src ?? null)}
                clickable={!!src}
                alt={alt}
                src={src}
                {...props}
            />
        ),
        // Add other overrides if necessary (e.g., for lists, headings if they appear in comments)
    };


    return (
        <>
            {/* Main comment container with left padding for indentation */}
            <div className="relative comment-container" style={{ paddingLeft: paddingLeft }}>
                {/* Threading line & Collapse Trigger Area */}
                {data.depth > 0 && (
                    <div
                        className="collapse-trigger absolute top-0 bottom-0 left-0 w-4 cursor-pointer group"
                        // Position line based on indentSize, centered in the previous level's indent space
                        style={{ left: `${(data.depth - 1) * indentSize + (indentSize / 2 - 1)}px` }}
                        onClick={handleToggleCollapse}
                        title={isCollapsed ? 'Expand thread' : 'Collapse thread'}
                    >
                        {/* The visual line */}
                        <span className={`absolute left-[0px] top-3 h-[calc(100%-1.5rem)] w-px bg-gray-200 dark:bg-gray-700 group-hover:bg-gray-400 dark:group-hover:bg-gray-500 transition-colors duration-100 ${isCollapsed ? 'hidden' : ''}`} aria-hidden="true"></span>
                    </div>
                )}

                {/* Inner container for the actual comment content */}
                {/* Adding a slight horizontal padding here can push text away from the line if needed,
                    but the increased indentSize might be sufficient. Let's try without first. */}
                {/* <div className="pl-2"> */}
                    {/* Comment Header */}
                    <div className="text-xs mb-1.5 flex items-center flex-wrap gap-x-2 text-gray-500 dark:text-gray-400">
                        {/* Collapse Button - make it part of the clickable trigger area */}
                        <button onClick={handleToggleCollapse} className="collapse-trigger text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 font-mono text-[10px] leading-none px-0.5 rounded focus:outline-none focus-visible:ring-1 focus-visible:ring-indigo-500 -ml-1 mr-1" title={isCollapsed ? 'Expand' : 'Collapse'} aria-expanded={!isCollapsed}>
                            {isCollapsed ? '[+]' : '[-]'}
                        </button>
                        <span className={`font-semibold ${isOp ? 'text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'}`}>{data.author}</span>
                        {isOp && <span className="text-[9px] font-bold text-blue-600 dark:text-blue-400 border border-blue-500 dark:border-blue-400 px-0.5 rounded-sm leading-none">OP</span>}
                        <span>{data.score} points</span>
                        <span title={new Date(data.created_utc * 1000).toLocaleString()}>{timeAgo(data.created_utc)}</span>
                    </div>

                    {/* Comment Body */}
                    {!isCollapsed && (
                        // Apply prose styles directly to the container holding ReactMarkdown
                        <div className="prose prose-sm dark:prose-invert max-w-none text-gray-800 dark:text-gray-200 leading-relaxed comment-body">
                            {data.body ? (
                                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                                    {data.body}
                                </ReactMarkdown>
                            ) : (
                                <p className="italic text-gray-400 dark:text-gray-500">[comment deleted]</p>
                            )}
                        </div>
                    )}
                {/* </div> */}

                {/* Render Replies Recursively */}
                {!isCollapsed && hasReplies && typeof data.replies === 'object' && data.replies.kind === 'Listing' && (
                    // --- CHANGE: Increased margin-top for more air ---
                    <div className="mt-8"> {/* Increased from mt-6 */}
                        <CommentTree comments={data.replies.data.children} postAuthor={postAuthor} onLoadMore={onLoadMore} loadingMoreId={loadingMoreId} indentSize={indentSize}/>
                    </div>
                )}
            </div>

            {/* Lightbox for this comment's images */}
             <Lightbox
                 open={lightboxOpen}
                 close={() => setLightboxOpen(false)}
                 slides={lightboxSlides}
                 plugins={[Zoom]}
                 zoom={{ scrollToZoom: true, maxZoomPixelRatio: 5 }}
                 // Disable prev/next buttons as we only show one image at a time from comments
                 render={{
                      buttonPrev: () => null,
                      buttonNext: () => null,
                 }}
             />
        </>
    );
};

export default Comment;